import { Component, Inject, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    AbstractControl,
    ReactiveFormsModule,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { FormGroupPipe } from 'src/app/shared/pipes/form-group.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import {
    loadCandidateContracts,
    loadCandidates,
    loadStates,
    loadTicketCategories,
    ticketCreate
} from 'src/app/root-store/ticket-store/actions/tickets.actions';
import {
    selectCandidateContracts,
    selectCandidates,
    selectStates,
    selectTicketCategories
} from 'src/app/root-store/ticket-store/tickets.selectors';
import { AsyncPipe, DatePipe, NgFor, NgIf } from '@angular/common';
import { debounceTime, tap } from 'rxjs/operators';
import { TicketCategory } from 'src/app/travelx/models/ticket-category.model';
import { Observable } from 'rxjs';
import { TicketCandidate } from 'src/app/travelx/models/ticket-candidate.model';
import { TicketCandidateContract } from 'src/app/travelx/models/ticket-candidate-contract.model';
import { CountryState } from 'src/app/shared/models/lookup-country-states.model';
import { SubcontractorService } from 'src/app/partner-network/subcontractor/subcontractor.service';
import { IdentityService } from 'src/app/core/identity.service';
import { SubcontractorPermissions } from 'src/app/partner-network/subcontractor/models/subcontractor-permissions.model';
import { SubCategoryTypes } from 'src/app/travelx/tickets/enums/tickets.enums';
import { CandidateService } from 'src/app/recruiting/candidates/candidate.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { RichEditorComponent } from 'src/app/shared/components/rich-editor/rich-editor';

@UntilDestroy()
@Component({
    selector: 'nova-create-new-ticket',
    templateUrl: './create-new-ticket.component.html',
    styleUrls: ['./create-new-ticket.component.scss'],
    standalone: true,
    imports: [
        AsyncPipe,
        DatePipe,
        FormGroupPipe,
        MatAutocompleteModule,
        MatButtonModule,
        MatCheckboxModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatSelectModule,
        NgFor,
        NgIf,
        ReactiveFormsModule,
        RichEditorComponent
    ]
})
export class CreateNewTicketComponent implements OnInit {
    ticketCategories: TicketCategory[] = [];
    subListCategoryId = null;
    employeeSearchForm = this._fb.group({
        searchName: [null],
        includeSubContr: [false],
        selectedContract: [{ value: null, disabled: true }, Validators.required]
    });

    ticketForm: UntypedFormGroup = this._fb.group({
        categories: this._fb.array([])
    });

    bookedDaysSubcategoryId = SubCategoryTypes.BookedDays;
    selectedCandidate: TicketCandidate = null;
    hasSubcontractorPermission = false;
    candidates$: Observable<TicketCandidate[]>;
    ticketContracts$: Observable<TicketCandidateContract[]>;
    states$: Observable<CountryState[]>;
    preventChanges: boolean = false;

    constructor(
        private readonly _store: Store,
        private readonly _fb: UntypedFormBuilder,
        private readonly _dialogRef: MatDialogRef<CreateNewTicketComponent>,
        private readonly _subService: SubcontractorService,
        private readonly _identityService: IdentityService,
        private readonly _candidateService: CandidateService,
        private readonly _featureManager: LDFeatureManager,
        @Inject(MAT_DIALOG_DATA)
        private readonly _data: {
            candidate: TicketCandidate;
            isSubcontractor: boolean;
            contractId: number;
            preventChanges: boolean;
            openNewTab: boolean;
        }
    ) {}

    ngOnInit(): void {
        this._subService
            .getPermissions(this._identityService.current.contactId)
            .pipe(untilDestroyed(this))
            .subscribe((perms: SubcontractorPermissions) => {
                this.hasSubcontractorPermission =
                    perms.hasSubcontractorDetailsPermission || perms.hasSubcontractorsPermission;
                this.employeeSearchForm
                    .get('includeSubContr')
                    .patchValue(this.hasSubcontractorPermission ?? false, { emitEvent: false });
            });

        this._store.dispatch(loadTicketCategories());
        this._store.dispatch(loadStates());
        this._store
            .select(selectTicketCategories)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (categories) =>
                    (this.ticketCategories = categories.filter((x) => x.id !== SubCategoryTypes.BookedDays))
            });

        this.employeeSearchForm
            .get('searchName')
            .valueChanges.pipe(debounceTime(300), untilDestroyed(this))
            .subscribe({
                next: (value) => {
                    this._store.dispatch(
                        loadCandidates({
                            query: value,
                            subContract: this.employeeSearchForm.get('includeSubContr').value
                        })
                    );
                    this.updateSelectedContractState();
                }
            });

        this.candidates$ = this._store.select(selectCandidates);

        this.ticketContracts$ = this._store.select(selectCandidateContracts).pipe(
            tap((contracts) => {
                if (this._data?.contractId) {
                    const passedInContract: TicketCandidateContract = contracts.find(
                        (contract) => contract.id === this._data.contractId
                    );
                    this.employeeSearchForm.get('selectedContract').patchValue(passedInContract, { emitEvent: false });
                }
            })
        );

        this.states$ = this._store.select(selectStates);

        if (this._data?.candidate) {
            if (this._data.candidate.name) {
                this.employeeSearchForm.get('searchName').patchValue(this._data.candidate, { emitEvent: false });
            } else if (this._data.candidate.id) {
                this._candidateService
                    .getCandidateData(this._data.candidate.id)
                    .pipe(untilDestroyed(this))
                    .subscribe((result) => {
                        this.employeeSearchForm.get('searchName').patchValue(
                            {
                                name: `${result.firstName} ${result.lastName}`,
                                id: Number(result.userId)
                            } as TicketCandidate,
                            { emitEvent: false }
                        );
                    });
            }
            this.candidateSelected(null);
        }

        this.preventChanges = this._data?.preventChanges ?? false;
        if (this.preventChanges) {
            this.employeeSearchForm.get('searchName').disable({ emitEvent: false });
            this.employeeSearchForm.get('selectedContract').disable({ emitEvent: false });
        }
    }

    getCategories(): UntypedFormArray {
        return this.ticketForm.controls.categories as UntypedFormArray;
    }

    getCategory(i: number): TicketCategory {
        return (
            this.ticketCategories.find((data) => data.id === this.getCategories().controls[i].value.categoryId) || null
        );
    }

    getSubCategory(i: number): TicketCategory {
        return (
            this.ticketCategories.find((data) => data.id === this.getCategories().controls[i].value.subCategoryId) ||
            null
        );
    }

    candidateSelected(option: MatAutocompleteSelectedEvent): void {
        this.selectedCandidate = option?.option?.value ?? this._data.candidate;
        this.employeeSearchForm.get('selectedContract').patchValue(null, { emitEvent: false });
        this.updateSelectedContractState();
        this._store.dispatch(loadCandidateContracts({ candidateId: this.selectedCandidate.id }));
    }

    removeItem(i: number): void {
        this.getCategories().removeAt(i);
    }

    candidateDisplay(employee: TicketCandidate): string {
        return employee?.name;
    }

    setActive(id: number): void {
        this.subListCategoryId = id;
    }

    selectCategory(id: number, i: number): void {
        this.getCategories().controls[i]['controls'].categoryId.patchValue(id);
    }

    selectSubCategory(id: number, i: number): void {
        this.getCategories().controls[i]['controls'].categoryId.patchValue(this.subListCategoryId);
        this.getCategories().controls[i]['controls'].subCategoryId.patchValue(id);
    }

    createSubCategory(id: number): void {
        const subCategory = this.ticketCategories.find((data) => data.id === id);
        const category = this.ticketCategories.find((data) => data.id === this.subListCategoryId);
        const isAutoLock = subCategory?.isAutoLock || category?.isAutoLock;
        this.getCategories().push(this.generateCategoryForm(id, this.subListCategoryId, isAutoLock));
    }

    createTicket(): void {
        // we're getting values from RawValue since there are scenarios where some of the FormControls are disabled but still have a value to read
        const formValues = this.employeeSearchForm.getRawValue();

        const ticketPayload = {
            candidateId: this.selectedCandidate.id,
            categories: this.ticketForm.value.categories,
            contractId: formValues.selectedContract.id,
            isSubContractor: formValues.includeSubContr,
            unitId: formValues.selectedContract.unitId
        };

        this._store.dispatch(ticketCreate({ ticket: ticketPayload, openNewTab: this._data?.openNewTab }));
        this._dialogRef.close();
    }

    close(): void {
        this._dialogRef.close();
    }

    private generateCategoryForm(subCategoryId, categoryId, isAutoLock: boolean): AbstractControl {
        return this._fb.group({
            categoryId: [categoryId, Validators.required],
            subCategoryId: [subCategoryId, Validators.required],
            categoryNote: [''],
            stateId: [null],
            bookedDays: [''],
            isAutoLock: [isAutoLock]
        });
    }

    private updateSelectedContractState(): void {
        const selectedContractControl = this.employeeSearchForm.get('selectedContract');
        const searchNameControl = this.employeeSearchForm.get('searchName');
        if (searchNameControl.value) {
            selectedContractControl.enable();
        } else {
            selectedContractControl.disable();
        }
    }
}
